/**
 * ConversationThread Component
 * Phase 6: Communication & Engagement
 * Chat-style conversation view with real-time updates
 */

import { useState, useEffect, useRef } from 'react';
import { useConversation } from '../hooks/useMessaging';
import {
  ArrowLeft,
  Send,
  Paperclip,
  MoreVertical,
  Archive,
  Ban,
  FileText,
  Download,
  CheckCheck,
  Check,
  Loader2,
  Building2,
  ExternalLink
} from 'lucide-react';

// Format timestamp
function formatMessageTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatMessageDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }
}

// Group messages by date
function groupMessagesByDate(messages) {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  messages.forEach(message => {
    const messageDate = new Date(message.created_at).toDateString();

    if (messageDate !== currentDate) {
      currentDate = messageDate;
      currentGroup = {
        date: message.created_at,
        messages: []
      };
      groups.push(currentGroup);
    }

    currentGroup.messages.push(message);
  });

  return groups;
}

// Attachment component
function MessageAttachment({ attachment }) {
  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return 'PDF';
    if (type?.includes('powerpoint') || type?.includes('presentation')) return 'PPT';
    if (type?.includes('word') || type?.includes('document')) return 'DOC';
    if (type?.includes('excel') || type?.includes('sheet')) return 'XLS';
    if (type?.includes('image')) return 'IMG';
    return 'FILE';
  };

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-3 py-2 bg-white/80 rounded-lg hover:bg-white transition-colors"
    >
      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-600">
        {getFileIcon(attachment.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {attachment.name}
        </div>
        {attachment.size && (
          <div className="text-xs text-gray-500">
            {(attachment.size / 1024).toFixed(1)} KB
          </div>
        )}
      </div>
      <Download className="w-4 h-4 text-gray-400" />
    </a>
  );
}

// Message bubble
function MessageBubble({ message, isMine, showAvatar }) {
  const hasAttachments = message.attachments && message.attachments.length > 0;

  return (
    <div className={`flex items-end gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {showAvatar ? (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isMine ? 'bg-purple-100' : 'bg-gray-100'
        }`}>
          <Building2 className={`w-4 h-4 ${isMine ? 'text-purple-600' : 'text-gray-500'}`} />
        </div>
      ) : (
        <div className="w-8" />
      )}

      {/* Message content */}
      <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'}`}>
        {/* Subject (if present and first message) */}
        {message.subject && (
          <div className={`text-xs font-medium mb-1 ${isMine ? 'text-right text-purple-600' : 'text-gray-600'}`}>
            {message.subject}
          </div>
        )}

        {/* Bubble */}
        <div className={`rounded-2xl px-4 py-2 ${
          isMine
            ? 'bg-purple-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        }`}>
          {/* Body */}
          <div className="whitespace-pre-wrap text-sm">
            {message.body}
          </div>

          {/* Attachments */}
          {hasAttachments && (
            <div className="mt-2 space-y-2">
              {message.attachments.map((att, index) => (
                <MessageAttachment key={index} attachment={att} />
              ))}
            </div>
          )}
        </div>

        {/* Timestamp and read status */}
        <div className={`flex items-center gap-1 mt-1 text-xs text-gray-500 ${isMine ? 'justify-end' : ''}`}>
          <span>{formatMessageTime(message.created_at)}</span>
          {isMine && (
            message.is_read
              ? <CheckCheck className="w-3 h-3 text-blue-500" />
              : <Check className="w-3 h-3" />
          )}
        </div>
      </div>
    </div>
  );
}

// Date separator
function DateSeparator({ date }) {
  return (
    <div className="flex items-center gap-4 py-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-xs font-medium text-gray-500">
        {formatMessageDate(date)}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

// Quick reply input
function QuickReply({ onSend, sending, disabled }) {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  const handleSend = () => {
    if (!text.trim() || sending || disabled) return;
    onSend({ body: text.trim() });
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInput = (e) => {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <div className="flex items-end gap-2 p-4 border-t border-gray-200 bg-white">
      {/* Attachment button */}
      <button
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        disabled={disabled}
      >
        <Paperclip className="w-5 h-5" />
      </button>

      {/* Input */}
      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={sending || disabled}
          rows={1}
          className="w-full px-4 py-2 border border-gray-200 rounded-2xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50"
          style={{ maxHeight: '120px' }}
        />
      </div>

      {/* Send button */}
      <button
        onClick={handleSend}
        disabled={!text.trim() || sending || disabled}
        className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {sending ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Send className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}

// Header with conversation info
function ConversationHeader({ conversation, userType, onBack, onArchive, onBlock }) {
  const [showMenu, setShowMenu] = useState(false);

  const otherPartyName = userType === 'seller'
    ? conversation.buyer_company
    : (conversation.seller_company || conversation.seller_company_cn);

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
      {/* Back button */}
      <button
        onClick={onBack}
        className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Avatar */}
      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
        <Building2 className="w-5 h-5 text-purple-600" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 truncate">{otherPartyName}</h3>
        {conversation.asset_name && (
          <p className="text-sm text-gray-500 truncate">
            Discussing: {conversation.asset_name}
          </p>
        )}
      </div>

      {/* Actions menu */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <MoreVertical className="w-5 h-5" />
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-20 py-1">
              <button
                onClick={() => {
                  onArchive();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Archive className="w-4 h-4" />
                Archive Conversation
              </button>
              {userType === 'buyer' && (
                <button
                  onClick={() => {
                    onBlock();
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Ban className="w-4 h-4" />
                  Block Seller
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Main ConversationThread component
export function ConversationThread({
  conversationId,
  userType,
  onBack,
  onArchive,
  onBlock,
  showHeader = true,
  className = ''
}) {
  const { conversation, messages, loading, sending, error, sendMessage } = useConversation(
    conversationId,
    userType
  );
  const messagesEndRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Group messages by date
  const messageGroups = groupMessagesByDate(messages);

  if (!conversationId) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 ${className}`}>
        <div className="text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Select a conversation to view messages</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-white ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-white ${className}`}>
        <div className="text-center text-red-500">
          <p className="font-medium">Error loading conversation</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className={`flex items-center justify-center bg-white ${className}`}>
        <p className="text-gray-500">Conversation not found</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-white ${className}`}>
      {/* Header */}
      {showHeader && (
        <ConversationHeader
          conversation={conversation}
          userType={userType}
          onBack={onBack}
          onArchive={onArchive}
          onBlock={onBlock}
        />
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messageGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <DateSeparator date={group.date} />
            <div className="space-y-3">
              {group.messages.map((message, messageIndex) => {
                const isMine = message.sender_type === userType;
                const prevMessage = messageIndex > 0 ? group.messages[messageIndex - 1] : null;
                const showAvatar = !prevMessage || prevMessage.sender_type !== message.sender_type;

                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isMine={isMine}
                    showAvatar={showAvatar}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No messages yet</p>
            <p className="text-sm text-gray-400 mt-1">Send the first message to start the conversation</p>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick reply input */}
      <QuickReply
        onSend={sendMessage}
        sending={sending}
        disabled={false}
      />
    </div>
  );
}

export default ConversationThread;
